import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid as MuiGrid,
  Avatar,
  // Alert,
  // Link,
} from '@mui/material';
import { motion } from 'framer-motion';

const MotionPaper = motion.create(Paper);
const Grid = MuiGrid as any;

const About = () => {
  return (
    <>
      {/* <Alert severity="info" sx={{ mb: 2 }}>
        Hello, I'm currently testing{' '}
        <Link href="https://aws.amazon.com/cognito/" target="_blank" rel="noopener noreferrer">
          Cognito
        </Link>
      </Alert> */}
      <Box sx={{ py: { xs: 2, md: 4 } }}>
        <Grid container spacing={4} sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              sx={{
                p: { xs: 2, md: 3 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: 'background.paper',
                borderRadius: 2,
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 120, md: 200 },
                  height: { xs: 120, md: 200 },
                  mb: 2,
                  border: '4px solid',
                  borderColor: 'primary.main',
                }}
                alt="Michael Suliot"
                src="/images/profile.jpg"
              />
              <Typography variant="h4" component="h1" gutterBottom>
                Michael Suliot
              </Typography>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Technology Executive | Scalable AI Systems | Cloud Architecture | Strategic Transformation
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                Gilbert, AZ 85298 | 480-773-5608 | michael@suliot.com
              </Typography>
            </MotionPaper>
          </Grid>
          
          <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 8' } }}>
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              sx={{
                p: 4,
                backgroundColor: 'background.paper',
                borderRadius: 2,
              }}
            >
              <Typography variant="h5" component="h2" gutterBottom>
                About Me
              </Typography>
              <Typography variant="body1" paragraph>
                I am a Technology Executive with over 30 years of experience architecting intelligent, adaptive systems that power high-growth enterprises. As CTO at ARMLS, I lead AI research and cloud transformation that delivers measurable innovation, performance, and long-term value.
              </Typography>
              <Typography variant="body1" paragraph>
                I also founded and built Jobriq, an AI-powered career optimization platform that helps candidates evaluate role fit and optimize resumes, from concept through production as sole architect and developer.
              </Typography>
              <Typography variant="body1" paragraph>
                My work spans AI/ML, Retrieval-Augmented Generation (RAG), large-scale data systems, and AWS cloud architecture. I have led enterprise transformations, scaled high-performing engineering teams, and delivered impactful products. My leadership combines technical depth, strategic clarity, and a deep commitment to customer outcomes.
              </Typography>
              <Typography variant="body1" paragraph>
                Outside of work, I enjoy exploring the Southwest, trail hiking, and experimenting with emerging AI tools, always staying curious in both nature and technology.
              </Typography>

              {/* Key Highlights - now directly under summary */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Key Highlights
                </Typography>
                <Grid container spacing={2}>
                  <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: 'background.default',
                        minHeight: 100,
                      }}
                    >
                      <Typography variant="h6" color="primary">
                        AI Innovation
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Leading AI R&amp;D in LLMs, RAG, and real-time document intelligence
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: 'background.default',
                        minHeight: 100,
                      }}
                    >
                      <Typography variant="h6" color="primary">
                        AWS Architecture
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Designing scalable AWS infrastructure for enterprise-grade systems
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: 'background.default',
                        minHeight: 100,
                      }}
                    >
                      <Typography variant="h6" color="primary">
                        Product Development
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Delivering customer-first solutions through scalable architectures
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: 'background.default',
                        minHeight: 100,
                      }}
                    >
                      <Typography variant="h6" color="primary">
                        Leadership
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Scaling teams, systems, and strategy to deliver customer-focused innovation
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* What I Believe */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                What I Believe
              </Typography>
              <Typography variant="body1" paragraph>
                I believe AI should amplify human intelligence, not replace it. I focus on building responsible, transparent systems that are scalable, interpretable, and tightly aligned with the real-world decisions they empower.
              </Typography>
              <Typography variant="body1" paragraph>
                I build not just for today's needs but for tomorrow's complexity, ensuring infrastructure, teams, and models are built to adapt, scale, and sustain meaningful impact.
              </Typography>
              <Typography variant="body1" paragraph>
                I thrive at the intersection of innovation and impact. Whether scaling platforms, leading AI breakthroughs, or transforming cloud ecosystems, I build with purpose, lead with integrity, and deliver with excellence. I am always exploring opportunities to drive bold ideas into reality with world-class teams.
              </Typography>

              {/* Results That Matter */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Results That Matter
              </Typography>
              <Grid container spacing={2}>
                <Grid sx={{ gridColumn: { xs: 'span 6', md: 'span 3' } }}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.default' }}>
                    <Typography variant="h5" color="primary">10x</Typography>
                    <Typography variant="body2">Revenue Growth Enabled by AI</Typography>
                  </Paper>
                </Grid>
                <Grid sx={{ gridColumn: { xs: 'span 6', md: 'span 3' } }}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.default' }}>
                    <Typography variant="h5" color="primary">$12M</Typography>
                    <Typography variant="body2">Annual Tech Budget Managed</Typography>
                  </Paper>
                </Grid>
                <Grid sx={{ gridColumn: { xs: 'span 6', md: 'span 3' } }}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.default' }}>
                    <Typography variant="h5" color="primary">40+</Typography>
                    <Typography variant="body2">Team Members Scaled and Led</Typography>
                  </Paper>
                </Grid>
                <Grid sx={{ gridColumn: { xs: 'span 6', md: 'span 3' } }}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.default' }}>
                    <Typography variant="h5" color="primary">3</Typography>
                    <Typography variant="body2">Mission-Critical AI Products Delivered</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Snapshot: Credentials & Impact */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4, color: 'common.white' }}>
                At a Glance
              </Typography>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'common.white', mb: 0.5 }}>
                  Education & Training
                </Typography>
                <Typography variant="body2" sx={{ color: 'common.white', mb: 2 }}>
                  Deep Learning AI (2023), Data Science Dojo (2017), DeVry University (1997)
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'common.white', mb: 0.5 }}>
                  Certifications
                </Typography>
                <Typography variant="body2" sx={{ color: 'common.white', mb: 2 }}>
                  AWS Solutions Architect, AWS AI Practitioner, CISSP, GPEN, MCDBA, MCSE
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'common.white', mb: 0.5 }}>
                  Speaking & Leadership
                </Typography>
                <Typography variant="body2" sx={{ color: 'common.white', mb: 2 }}>
                  Speaker on Generative AI, RAG, and data security; Board & strategy sessions
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'common.white', mb: 0.5 }}>
                  Technical Skills
                </Typography>
                <Typography variant="body2" sx={{ color: 'common.white' }}>
                  AWS, Gen AI/ML, RAG, Python, React, Node, Postgres, MongoDB, Kubernetes, CI/CD, Terraform, 
                </Typography>
              </Box>

              {/* Quote Block */}
              <Paper sx={{ mt: 4, p: 3, borderLeft: '5px solid', borderColor: 'primary.main', backgroundColor: 'background.default' }}>
                <Typography variant="body1" fontStyle="italic">
                  "The future does not belong to those who follow. It belongs to those who rethink, rebuild, and refuse to settle."
                </Typography>
                <Typography variant="body2" align="right" sx={{ mt: 1 }}>
                  - Michael Suliot, Technology Executive
                </Typography>
              </Paper>
            </MotionPaper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default About; 